import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Head from "../components/Head";
import auth from '../utils/auth';
import '../styles/ForgotPasswordPage.css';
import { scrollToTop } from '../hooks/useScrollToTop';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [cooldownActive, setCooldownActive] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated()) {
      navigate('/');
      scrollToTop();
    }
    
    const storedCooldown = localStorage.getItem('resetPasswordCooldown');
    if (storedCooldown) {
      const cooldownExpiry = parseInt(storedCooldown, 10);
      const currentTime = Date.now();
      
      if (cooldownExpiry > currentTime) {
        setCooldownTime(Math.ceil((cooldownExpiry - currentTime) / 1000));
        setCooldownActive(true);
        
        const timerId = setInterval(() => {
          setCooldownTime(prevTime => {
            if (prevTime <= 1) {
              clearInterval(timerId);
              setCooldownActive(false);
              localStorage.removeItem('resetPasswordCooldown');
              return 0;
            }
            return prevTime - 1;
          });
        }, 1000);
        
        return () => clearInterval(timerId);
      } else {
        localStorage.removeItem('resetPasswordCooldown');
      }
    }
  }, [navigate]);

  const startCooldown = () => {
    const cooldownDuration = 120;
    const expiryTime = Date.now() + (cooldownDuration * 1000);
    
    localStorage.setItem('resetPasswordCooldown', expiryTime.toString());
    
    setCooldownTime(cooldownDuration);
    setCooldownActive(true);
    
    const timerId = setInterval(() => {
      setCooldownTime(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timerId);
          setCooldownActive(false);
          localStorage.removeItem('resetPasswordCooldown');
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    if (cooldownActive) {
      setError(`Vänta ${formatTime(cooldownTime)} innan du skickar en ny återställningslänk.`);
      return;
    }

    if (!email) {
      setError('E-postadressen är obligatorisk.');
      return;
    }

    const emailRegex = /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/gi;
    if (!emailRegex.test(email)) {
      setError('Ange en giltig e-postadress.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );
      
      const data = await response.json();
      
      if (response.status !== 200) {
        setError(data.message || 'Ett fel inträffade. Försök igen.');
        setIsSubmitting(false);
        return;
      }
      
      setMessage(data.message);
      startCooldown();
    } catch (err) {
      setError('Ett serverfel inträffade. Försök igen senare.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <Head title="EssayWriter | Glömt lösenord" />
      <div className="forgot-password-container">
        <h2 className="forgot-password-title">Glömt lösenord</h2>
        {error && <p className="forgot-password-error">{error}</p>}
        {message && <p className="forgot-password-success">{message}</p>}
        <form onSubmit={handleSubmit} className="forgot-password-form">
          <div className="forgot-password-form-group">
            <label htmlFor="email" className="forgot-password-label">E-post:</label>
            <input
              type="email"
              id="email"
              className="forgot-password-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <button 
            type="submit" 
            className="forgot-password-button" 
            disabled={isSubmitting || cooldownActive}
          >
            {cooldownActive 
              ? `Vänta (${formatTime(cooldownTime)})` 
              : isSubmitting 
                ? 'Skickar...' 
                : 'Skicka återställningslänk'}
          </button>
        </form>
        <p className="forgot-password-login-link">
          Kom du ihåg ditt lösenord? <a href="/logga-in">Logga in</a>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;